import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getPiece, patchPiece, type PieceDetail } from '../api'
import { pieceFields } from './adopt'
import { createAutosave, dropSaved, type PieceField, type PieceFields } from './autosave'

export const pieceKey = (id: number) => ['editorial-piece', id] as const

/** The fields typed on the page but not saved yet (the body lives in the editor instead). */
export type LocalFields = Omit<PieceFields, 'body_html'>
export type LocalField = keyof LocalFields

/**
 * The page's one autosave (see autosave.ts), wired to the piece's cache: each saved copy goes
 * into the query cache, and `local` holds what was typed until the server's copy matches it.
 */
export function usePieceAutosave(initial: PieceDetail) {
  const qc = useQueryClient()
  const id = initial.id
  const [local, setLocal] = useState<LocalFields>({})
  const [autosave] = useState(() => createAutosave<PieceDetail>({
    base: initial.updated_at,
    held: pieceFields(initial),
    fieldsOf: pieceFields,
    save: (body) => patchPiece(id, body),
    onSaved: (result, sent) => {
      // A refetch that started before this save must not land after it with the older copy.
      void qc.cancelQueries({ queryKey: pieceKey(id), exact: true }).then(() => {
        qc.setQueryData(pieceKey(id), result)
        setLocal((l) => dropSaved(l, sent) as LocalFields)
      })
    },
    recover: () => getPiece(id),
    // The live article changed under an edit draft: read the piece again, so it shows as stale
    // (read-only, with "Start again from the live version").
    onEditStale: () => { void qc.invalidateQueries({ queryKey: pieceKey(id) }) },
    onRecovered: (fresh) => {
      void qc.cancelQueries({ queryKey: pieceKey(id), exact: true }).then(() => qc.setQueryData(pieceKey(id), fresh))
    },
  }))
  const snapshot = useSyncExternalStore(autosave.subscribe, autosave.getSnapshot)

  // Leaving the page sends what is still waiting rather than dropping it.
  useEffect(() => () => { void autosave.flush() }, [autosave])

  const set = useCallback(<F extends LocalField>(field: F, value: NonNullable<LocalFields[F]>, opts?: { immediate?: boolean }) => {
    setLocal((l) => ({ ...l, [field]: value }))
    autosave.change({ [field]: value } as PieceFields, opts)
  }, [autosave])

  const setBody = useCallback((html: string) => autosave.change({ body_html: html }), [autosave])

  /** Forget what was typed for these fields, in the page and in the autosave (a copy from elsewhere replaced them). */
  const forget = useCallback((...fields: PieceField[]) => {
    for (const f of fields) autosave.discard(f)
    setLocal((l) => {
      const out = { ...l }
      for (const f of fields) delete (out as Record<string, unknown>)[f]
      return out
    })
  }, [autosave])

  /** Drop every unsent edit and start again from the copy saved at `base`. */
  const reset = useCallback((fresh: PieceDetail) => {
    autosave.reset(fresh.updated_at, pieceFields(fresh))
    setLocal({})
  }, [autosave])

  return { autosave, snapshot, local, set, setBody, forget, reset }
}
